import { useAuth } from "@clerk/clerk-react";
import { Navigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { env } from "@/shared/config/env";
import { Spinner } from "@/shared/ui";
import { resolveAuthGate } from "../lib/guard";

/**
 * Gates the protected app shell. While Clerk resolves the session we render a
 * full-screen spinner — never the children — so protected UI never flashes
 * before auth is known. Signed-out users are redirected to `/sign-in`.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { isLoaded, isSignedIn } = useAuth();

  // Dev-only bypass: render the protected app without a Clerk session (pair with
  // the backend's DEV_AUTH_BYPASS). Compiled out of production builds via env.
  if (env.AUTH_BYPASS) return <>{children}</>;

  const gate = resolveAuthGate({ isLoaded, isSignedIn });

  if (gate === "loading") {
    return (
      <div className="grid min-h-screen place-items-center bg-canvas">
        <Spinner size={22} label={t("common.loading")} />
      </div>
    );
  }

  if (gate === "anon") {
    return <Navigate to="/sign-in" />;
  }

  return <>{children}</>;
}
